import React, { useState, useEffect, useContext, useRef } from "react";
import { UserContext } from "../../context/UserContext";
import { profilesApi, usersApi } from "../../api";
import "../../css/components/AvatarUploader.css";
import HrnbButton from "../controls/HrnbButton";
import { DeleteIcon, UploadIcon } from "../../icons";
import HrnbImage from "../controls/HrnbImage";
import HrnbContainer, { HrnbRow } from "../controls/HrnbContainer";
import HrnbFileUpload from "../controls/HrnbFileUpload";
import HrnbAlert from "../controls/HrnbAlert";
import HrnbModal from "../controls/HrnbModal";
import { DefaultAvatar } from "../../images";

export default function AvatarUploader({ profileId, onAvatarUpdate }) {
    const { setCurrentUser } = useContext(UserContext);
    const [selectedFile, setSelectedFile] = useState(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    // Автоисчезновение success-сообщения
    const successTimerRef = useRef(null);
    useEffect(() => {
        if (success) {
            clearTimeout(successTimerRef.current);
            successTimerRef.current = setTimeout(() => setSuccess(''), 3000);
        }
        return () => clearTimeout(successTimerRef.current);
    }, [success]);

    useEffect(() => {
        let objectUrl = null;
        const fetchAvatar = async () => {
            if (!profileId) return;
            setFetching(true);
            setError("");
            try {
                const p = await profilesApi.get(profileId);
                const avatarId = p?.avatar;
                if (!avatarId) {
                    setAvatarUrl(null);
                    setFetching(false);
                    return;
                }
                const blob = await profilesApi.getAvatar(avatarId);
                objectUrl = URL.createObjectURL(blob);
                setAvatarUrl(objectUrl);
            } catch (err) {
                console.error('[AvatarUploader] Failed to load avatar:', err.message);
                setError("Не удалось загрузить аватар");
                setAvatarUrl(null);
            } finally {
                setFetching(false);
            }
        };

        fetchAvatar();

        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [profileId]);

    const handleFileSelect = (e) => {
        const file = e?.target?.files?.[0] || null;
        setSelectedFile(file);
    };

    const handleUploadAvatar = async () => {
        if (!selectedFile || !profileId) return;
        setUploading(true);
        setError("");
        setSuccess("");

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const updatedProfile = await profilesApi.uploadAvatar(profileId, formData);
            const avatarId = updatedProfile?.avatar;

            if (avatarId) {
                const blob = await profilesApi.getAvatar(avatarId);
                const objectUrl = URL.createObjectURL(blob);
                setAvatarUrl(objectUrl);
            }

            // Колбэк для синхронизации профиля в родительском компоненте
            if (onAvatarUpdate) {
                onAvatarUpdate(updatedProfile);
            }

            // Обновляем данные текущего пользователя
            try {
                const user = await usersApi.current();
                if (setCurrentUser) {
                    setCurrentUser(user);
                }
            } catch (e) {
                console.warn('[AvatarUploader] Failed to refresh user after upload:', e.message);
            }
            setSelectedFile(null);
            setSuccess('Аватар успешно загружен');
        } catch (err) {
            console.error('[AvatarUploader] Failed to upload avatar:', err.message);
            setError("Ошибка при загрузке аватара");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAvatar = async () => {
        if (!profileId) return;
        setDeleteModalOpen(false);
        setDeleting(true);
        setError("");
        setSuccess("");
        try {
            await profilesApi.deleteAvatar(profileId);
            setAvatarUrl(null);

            // Колбэк для синхронизации профиля в родительском компоненте
            if (onAvatarUpdate) {
                onAvatarUpdate({ avatar: null });
            }

            // Обновляем данные текущего пользователя
            try {
                const user = await usersApi.current();
                if (setCurrentUser) {
                    setCurrentUser(user);
                }
            } catch (e) {
                console.warn('[AvatarUploader] Failed to refresh user after delete:', e.message);
            }
            setSuccess('Аватар удалён');
        } catch (err) {
            console.error('[AvatarUploader] Failed to delete avatar:', err.message);
            setError("Ошибка при удалении аватара");
        } finally {
            setDeleting(false);
        }
    };

    const isBusy = fetching || uploading || deleting;

    return (
        <HrnbContainer>
            <HrnbRow className="hrnb-row--center hrnb-row--align-center">
                <HrnbImage src={avatarUrl || DefaultAvatar} size={350} rounded alt="Аватар пользователя" className="avatar-uploader__image" />
            </HrnbRow>

            <div className="avatar-controls">
                <HrnbFileUpload
                    label="Аватар"
                    accept="image/png, image/jpeg"
                    onChange={handleFileSelect}
                    multiple={false}
                    value={selectedFile}
                    disabled={isBusy}
                    hint="PNG или JPEG, до 2 МБ"
                />

                <HrnbButton
                    variant="primary"
                    onClick={handleUploadAvatar}
                    disabled={!selectedFile || isBusy}
                    icon={<UploadIcon />}
                >
                    {uploading ? "Загрузка..." : "Загрузить аватар"}
                </HrnbButton>

                <HrnbButton
                    variant="outline-danger"
                    onClick={() => setDeleteModalOpen(true)}
                    disabled={!avatarUrl || isBusy}
                    icon={<DeleteIcon />}
                >
                    {deleting ? "Удаление..." : "Удалить аватар"}
                </HrnbButton>
            </div>

            {success && <HrnbAlert type="success" style={{ marginTop: 12 }}>{success}</HrnbAlert>}
            {error && <HrnbAlert type="danger" style={{ marginTop: 12 }}>{error}</HrnbAlert>}

            <HrnbModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title="Удаление аватара"
            >
                <p style={{ color: '#e0e0e0' }}>
                    Вы уверены, что хотите удалить аватар? Это действие нельзя отменить.
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
                    <HrnbButton variant="outline" onClick={() => setDeleteModalOpen(false)}>
                        Отмена
                    </HrnbButton>
                    <HrnbButton variant="danger" onClick={handleDeleteAvatar}>
                        Удалить
                    </HrnbButton>
                </div>
            </HrnbModal>
        </HrnbContainer>
    );

}

